import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Row, Col, Button, Empty, Alert, Tag, Spin } from "antd";
import {
  FileTextOutlined,
  LoginOutlined,
  LogoutOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import EmployeeAttendanceService from "Services/EmployeeServices/JobServices/EmployeeAttendanceService";
import {
  AttendanceLogActionEnum,
  AttendenceLogActionLabel,
} from "../../../Constants/Enums/Attendance/AttendanceLogActionEnum";

function DayDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { date, attendanceId } = location.state || {};

  const {
    userInfo,
    status,
    workingHours,
    othersInfo,
    requiredHrsMet,
    attendanceRecords,
    handleHistory,
    formatTo12Hour,
    getDateDetail,
  } = EmployeeAttendanceService();

  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedDateRecord, setSelectedDateRecord] = useState(null);
  const [selectedDateLogs, setSelectedDateLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date && !attendanceId) {
      console.warn("No date or attendanceId provided. Redirecting to calendar.");
      navigate("/employeedashboard/attendance");
      return;
    }

    // If we have attendanceId, fetch the detailed record
    if (attendanceId) {
      fetchDateDetail(attendanceId);
    }
  }, [date, attendanceId, navigate]);

  // Function to fetch date detail using attendanceId
  const fetchDateDetail = async (id) => {
    try {
      setLoading(true);
      const response = await getDateDetail(id);
      if (response && response.data) {
        setSelectedDateRecord(response.data);
        setSelectedDateLogs(response.data.logs || []);
      }
    } catch (error) {
      console.error("❌ Error fetching date detail:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Format time function
  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Format date function
  const formatDate = (dateString) => {
    const displayDate = dateString ? new Date(dateString) : currentTime;
    return displayDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Get status badge color and text
  const getStatusBadge = (statusValue) => {
    switch (statusValue) {
      case 0:
        return { color: "green", text: "Present" };
      case 1:
        return { color: "red", text: "Absent" };
      case 2:
        return { color: "orange", text: "Half Day" };
      case 3:
        return { color: "blue", text: "Holiday" };
      default:
        return { color: "default", text: "Unknown" };
    }
  };

  // Get display date for header
  const displayDate = date || currentTime.toISOString().split("T")[0];
  const statusBadge = selectedDateRecord
    ? getStatusBadge(selectedDateRecord.status)
    : getStatusBadge(status);

  // Determine which logs to display
  const displayLogs = selectedDateRecord ? selectedDateLogs : attendanceRecords;

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Spin size="large" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Header Section with Back Button */}
      <div
        className="header"
        style={{
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 0",
        }}
      >
        <Button
          className="btn-back"
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            handleHistory();
          }}
          style={{ marginRight: "20px" }}
        >
          Back to Calendar
        </Button>

        <div className="user-info" style={{ display: "flex", alignItems: "center", flex: 1 }}>
          <div
            className="avatar"
            style={{
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              backgroundColor: "#0b6a2f",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "18px",
              fontWeight: "bold",
              marginRight: "12px",
            }}
          >
            {userInfo.fullName?.charAt(0)}
          </div>
          <div>
            <h3 className="mb-0" style={{ color: "#333" }}>
              {userInfo.fullName}
            </h3>
            <p className="text-muted mb-0" style={{ color: "#6c757d" }}>
              Employee ID: {localStorage.getItem("personalId") || "N/A"}
            </p>
          </div>
        </div>

        <div className="date-display" style={{ textAlign: "right" }}>
          <h4 id="current-date" className="mb-0" style={{ color: "#333" }}>
            {formatDate(displayDate)}
          </h4>
          <p id="current-time" className="text-muted mb-0" style={{ color: "#6c757d" }}>
            {currentTime.toLocaleTimeString()}
          </p>
          {/* Show indicator if viewing a specific date from calendar */}
          {date && (
            <p className="text-info small mb-0">
              <InfoCircleOutlined /> Viewing specific date from calendar
            </p>
          )}
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* Left Column - Main Content */}
        <Col xs={24} lg={16}>
          {/* Status Card */}
          <Card className="card-professional">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="card-title mb-0">
                  {date ? `${formatDate(date)} Status` : "Today's Status"}
                </h4>
                <Tag color={statusBadge.color} style={{ fontSize: "14px", padding: "4px 12px" }}>
                  {statusBadge.text}
                </Tag>
              </div>

              {/* Display record data if available */}
              {selectedDateRecord ? (
                <div className="row mt-4">
                  <div className="col-md-6">
                    <p>
                      <ClockCircleOutlined className="me-2" />
                      First Check-in: <strong>{formatTime(selectedDateRecord.firstCheckIn)}</strong>
                    </p>
                    <p>
                      <ClockCircleOutlined className="me-2" />
                      Last Check-out: <strong>{formatTime(selectedDateRecord.lastCheckOut)}</strong>
                    </p>
                    <p>
                      <ClockCircleOutlined className="me-2" />
                      Total Worked: <strong>{selectedDateRecord.totalWorkedHours}</strong>
                    </p>
                    <p>
                      <ClockCircleOutlined className="me-2" />
                      Activity Windows: <strong>{selectedDateRecord.totalWindows || 0}</strong>
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p>
                      {selectedDateRecord.isLate ? (
                        <CloseCircleOutlined className="me-2 text-danger" />
                      ) : (
                        <CheckCircleOutlined className="me-2 text-success" />
                      )}
                      Late Arrival: <strong>{selectedDateRecord.isLate ? "Yes" : "No"}</strong>
                    </p>
                    <p>
                      {selectedDateRecord.isEarlyLeave ? (
                        <CloseCircleOutlined className="me-2 text-danger" />
                      ) : (
                        <CheckCircleOutlined className="me-2 text-success" />
                      )}
                      Early Departure:{" "}
                      <strong>{selectedDateRecord.isEarlyLeave ? "Yes" : "No"}</strong>
                    </p>
                    <p>
                      <InfoCircleOutlined className="me-2" />
                      Remarks: <Tag color="orange">{selectedDateRecord.remarks}</Tag>
                    </p>
                    {selectedDateRecord.overtimeHours !== "00:00:00" && (
                      <p>
                        <ClockCircleOutlined className="me-2" />
                        Overtime: <strong>{selectedDateRecord.overtimeHours}</strong>
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                // Fallback to current day data
                <div className="row mt-4">
                  <div className="col-md-6">
                    <p>
                      <ClockCircleOutlined className="me-2" />
                      Working Hours:{" "}
                      <strong>
                        {" "}
                        {formatTo12Hour(workingHours.startTime)} -{" "}
                        {formatTo12Hour(workingHours.endTime)}
                      </strong>
                    </p>
                    <p>
                      <ClockCircleOutlined className="me-2" />
                      Grace Period: <strong> {workingHours.graceMinutes} minutes</strong>
                    </p>

                    {othersInfo.overtimeHours !== null && (
                      <p>
                        <ClockCircleOutlined className="me-2" />
                        Over Time Hours: <strong> {formatDate(othersInfo.overtimeHours)}</strong>
                      </p>
                    )}
                  </div>
                  <div className="col-md-6">
                    <p>
                      <InfoCircleOutlined className="me-2" />
                      Remarks: <span className="badge bg-warning">{othersInfo.remarks}</span>
                    </p>
                    <p>
                      <ClockCircleOutlined className="me-2" />
                      Hours Worked: <strong> {formatTo12Hour(othersInfo.totalWorkedHours)}</strong>
                    </p>
                    <p>
                      <ClockCircleOutlined className="me-2" />
                      Activity Windows: <strong>{othersInfo.totalWindows}</strong>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Activity Log Card */}
          <Card className="card-professional" style={{ marginTop: "12px" }}>
            <h4 className="card-title">
              {date ? `${formatDate(date)} Activity Log` : "Today's Activity Log"}
            </h4>
            <div className="logs-container">
              {displayLogs && displayLogs.length > 0 ? (
                displayLogs.map((log, index) => (
                  <div
                    key={index}
                    className="log-item"
                    style={{
                      padding: "12px",
                      border: "1px solid #f0f0f0",
                      borderRadius: "6px",
                      marginBottom: "8px",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        {log.action === AttendanceLogActionEnum.CheckIn ? (
                          <>
                            <LoginOutlined className="me-2" style={{ color: "#52c41a" }} />
                            <span
                              className="log-action"
                              style={{ color: "#52c41a", fontWeight: "bold" }}
                            >
                              {AttendenceLogActionLabel[log.action]}
                            </span>
                          </>
                        ) : (
                          <>
                            <LogoutOutlined className="me-2" style={{ color: "#ff4d4f" }} />
                            <span
                              className="log-action"
                              style={{ color: "#ff4d4f", fontWeight: "bold" }}
                            >
                              {AttendenceLogActionLabel[log.action]}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="log-time" style={{ fontWeight: "bold" }}>
                        {formatTime(log.checkTime)}
                      </div>
                    </div>
                    <div className="text-muted small mt-1">
                      {log.isManual ? "Manual Entry" : "Automatic"} • Device:{" "}
                      {log.deviceName || "Unknown"}
                    </div>
                  </div>
                ))
              ) : (
                <Empty
                  description={
                    selectedDateRecord
                      ? `No activity records found for ${formatDate(date)}`
                      : "No activity records yet"
                  }
                />
              )}
            </div>
          </Card>
        </Col>

        {/* Right Column - Sidebar */}
        <div className="col-lg-4">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-4">Working Hours</h4>
              <div className="working-hours mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span>Start Time:</span>
                  <strong>{formatTo12Hour(workingHours.startTime)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>End Time:</span>
                  <strong>{formatTo12Hour(workingHours.endTime)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Grace Period:</span>
                  <strong>{workingHours.graceMinutes} minutes</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Full Day Hours:</span>
                  <strong>{workingHours.fullDayRequiredHours} hours</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: "12px" }}>
            <div className="card-body">
              <h4 className="card-title mb-4">Quick Stats</h4>
              <div className="d-flex justify-content-between mb-3">
                <span>On Time Arrival:</span>
                <strong>
                  {selectedDateRecord
                    ? selectedDateRecord.isLate
                      ? "No"
                      : "Yes"
                    : othersInfo?.isLate
                    ? "No"
                    : "Yes"}
                </strong>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span>Late Arrival:</span>
                <strong>
                  {selectedDateRecord
                    ? selectedDateRecord.isLate
                      ? "Yes"
                      : "No"
                    : othersInfo?.isLate
                    ? "Yes"
                    : "No"}
                </strong>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span>Early Departure:</span>
                <strong>
                  {selectedDateRecord
                    ? selectedDateRecord.isEarlyLeave
                      ? "Yes"
                      : "No"
                    : othersInfo?.isEarlyLeave
                    ? "Yes"
                    : "No"}
                </strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Required Hours Met:</span>
                <strong>
                  {selectedDateRecord
                    ? selectedDateRecord.totalWorkedHours >=
                      selectedDateRecord.fullDayRequiredHours + ":00"
                      ? "Yes"
                      : "No"
                    : requiredHrsMet ?? "—"}
                </strong>
              </div>
            </div>
          </div>

          {/* History Button */}
          <Button
            className="btn-primary"
            onClick={() => {
              handleHistory();
            }}
            block
            size="large"
            style={{ marginTop: "12px" }}
          >
            <FileTextOutlined />
            View Full History
          </Button>

          {/* Back Button for mobile */}
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              handleHistory();
            }}
            block
            style={{ marginTop: "12px" }}
            className="d-lg-none"
          >
            Back to Calendar
          </Button>
        </div>
      </Row>
    </DashboardLayout>
  );
}

export default DayDetail;
